/**
 * Lightweight Markdown renderer for AI-generated notes.
 * Supports: # ## ### #### headings, **bold**, _italic_, ordered/unordered lists,
 * horizontal rule (---/***), paragraphs. Used by CourseReader and OrganPage.
 */

// Inline bold/italic renderer for **bold** and _italic_ / *italic*
export function renderInline(text, keyPrefix) {
  const parts = [];
  const regex = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const content = m[2] ?? m[3] ?? m[4];
    parts.push(
      <strong key={`${keyPrefix}-b-${i++}`} className="font-bold text-slate-900">
        {content}
      </strong>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}


// Strip markdown formatting for plain-text previews (used in collapsed lists).
export function stripMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/^\s*#{1,6}\s+/gm, "")           // headings
    .replace(/\*\*([^*]+)\*\*/g, "$1")         // **bold**
    .replace(/__([^_]+)__/g, "$1")             // __bold__
    .replace(/\*([^*]+)\*/g, "$1")             // *italic*
    .replace(/_([^_]+)_/g, "$1")               // _italic_
    .replace(/`([^`]+)`/g, "$1")               // `code`
    .replace(/^\s*([-*+])\s+/gm, "")           // bullet markers
    .replace(/^\s*\d+[.)]\s+/gm, "")           // ordered list markers
    .replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/gm, "") // rules
    .replace(/\n{2,}/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function MarkdownNotes({ text, compact = false }) {
  const lines = (text || "").split(/\r?\n/);
  const blocks = [];
  let list = null;

  const flushList = (key) => {
    if (!list) return;
    if (list.ordered) {
      blocks.push(
        <ol key={`ol-${key}`} start={list.start || 1} className="list-decimal pl-6 space-y-1.5 my-3 text-slate-700">
          {list.items.map((it, i) => (
            <li key={i} className="leading-[1.7] pl-1">
              {renderInline(it, `li-${key}-${i}`)}
            </li>
          ))}
        </ol>
      );
    } else {
      blocks.push(
        <ul key={`ul-${key}`} className="list-disc pl-6 space-y-1.5 my-3 text-slate-700">
          {list.items.map((it, i) => (
            <li key={i} className="leading-[1.7] pl-1">
              {renderInline(it, `li-${key}-${i}`)}
            </li>
          ))}
        </ul>
      );
    }
    list = null;
  };

  const pSize = compact
    ? "text-sm text-slate-700 leading-[1.7] my-2"
    : "text-base sm:text-[17px] text-slate-700 leading-[1.8] my-2.5";
  const h2Size = compact
    ? "text-base font-extrabold text-slate-900 mt-4 mb-1.5 tracking-tight"
    : "text-lg sm:text-xl font-extrabold text-slate-900 mt-6 mb-2 tracking-tight";
  const h3Size = compact
    ? "text-sm font-bold text-slate-900 mt-3 mb-1"
    : "text-base font-bold text-slate-900 mt-5 mb-1.5";
  const h4Size = compact
    ? "text-[13px] font-bold text-slate-900 mt-2.5 mb-0.5"
    : "text-sm font-bold text-slate-900 mt-4 mb-1";

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (!trimmed) {
      flushList(idx);
      return;
    }

    if (/^####\s+/.test(trimmed)) {
      flushList(idx);
      blocks.push(
        <h4 key={idx} className={h4Size}>
          {renderInline(trimmed.replace(/^####\s+/, ""), `h4-${idx}`)}
        </h4>
      );
      return;
    }
    if (/^###\s+/.test(trimmed)) {
      flushList(idx);
      blocks.push(
        <h3 key={idx} className={h3Size}>
          {renderInline(trimmed.replace(/^###\s+/, ""), `h3-${idx}`)}
        </h3>
      );
      return;
    }
    if (/^##\s+/.test(trimmed)) {
      flushList(idx);
      blocks.push(
        <h2 key={idx} className={h2Size}>
          {renderInline(trimmed.replace(/^##\s+/, ""), `h2-${idx}`)}
        </h2>
      );
      return;
    }
    if (/^#\s+/.test(trimmed)) {
      flushList(idx);
      blocks.push(
        <h2 key={idx} className={h2Size}>
          {renderInline(trimmed.replace(/^#\s+/, ""), `h1-${idx}`)}
        </h2>
      );
      return;
    }
    if (/^([-*])\s+/.test(trimmed)) {
      const item = trimmed.replace(/^([-*])\s+/, "");
      if (!list || list.ordered) {
        flushList(idx);
        list = { ordered: false, items: [] };
      }
      list.items.push(item);
      return;
    }
    if (/^\d+[.)]\s+/.test(trimmed)) {
      const num = parseInt(trimmed.match(/^(\d+)[.)]\s+/)[1], 10);
      const item = trimmed.replace(/^\d+[.)]\s+/, "");
      if (!list || !list.ordered) {
        flushList(idx);
        list = { ordered: true, start: num, items: [] };
      }
      list.items.push(item);
      return;
    }
    if (/^(---+|___+|\*\*\*+)$/.test(trimmed)) {
      flushList(idx);
      blocks.push(<hr key={idx} className="my-5 border-slate-100" />);
      return;
    }

    flushList(idx);
    blocks.push(
      <p key={idx} className={pSize}>
        {renderInline(trimmed, `p-${idx}`)}
      </p>
    );
  });
  flushList("end");

  return (
    <div className="reader-md" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {blocks}
    </div>
  );
}

export default MarkdownNotes;
